import dayjs from "dayjs";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Typography } from "antd";
import type { DashboardActivity } from "../../api-models";
import { CHART_AXIS_TICK, CHART_INK, CHART_MARKS, CHART_SERIES } from "./chartTheme";
import { ChartTooltip } from "./ChartTooltip";

/**
 * Document throughput over the last 30 days, faceted into one small chart per stream.
 *
 * Small multiples rather than four lines on one plot: at daily granularity these series
 * spend most of the month near zero and converge, so a single plot would need four hues
 * and four end-labels fighting over the same corner. Faceting makes each stream a single
 * series in a single colour and lets the reader compare shapes instead of untangling
 * lines — and the y scale is deliberately shared across every facet, so a tall panel
 * really does mean more documents than a short one.
 */
export function ActivityTrend({ activity }: { activity: DashboardActivity }) {
  if (activity.series.length === 0) return null;

  // One shared domain, rounded up, so panel heights stay comparable between facets.
  const peak = Math.max(1, ...activity.series.flatMap((s) => s.counts));

  return (
    <div className="glut-chart-facets">
      {activity.series.map((series) => {
        const data = activity.days.map((day, i) => ({ day, count: series.counts[i] ?? 0 }));
        const total = series.counts.reduce((sum, n) => sum + n, 0);
        return (
          <div key={series.key}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
              <Typography.Text style={{ fontSize: 12, color: CHART_INK.secondary }}>{series.label}</Typography.Text>
              <Typography.Text strong style={{ fontSize: 13 }}>
                {total}
              </Typography.Text>
            </div>
            <ResponsiveContainer width="100%" height={110}>
              <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                <CartesianGrid vertical={false} stroke={CHART_INK.gridline} />
                <XAxis
                  dataKey="day"
                  tick={CHART_AXIS_TICK}
                  tickFormatter={(v: string) => dayjs(v).format("D MMM")}
                  axisLine={{ stroke: CHART_INK.axis }}
                  tickLine={false}
                  minTickGap={28}
                />
                <YAxis
                  domain={[0, peak]}
                  width={28}
                  tick={CHART_AXIS_TICK}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  cursor={{ stroke: CHART_INK.axis, strokeWidth: 1 }}
                  content={({ active, payload, label }) =>
                    active && payload?.length ? (
                      <ChartTooltip
                        title={dayjs(label as string).format("D MMM YYYY")}
                        rows={[{ color: CHART_SERIES[0], label: series.label, value: payload[0].value as number }]}
                      />
                    ) : null
                  }
                />
                <Area
                  dataKey="count"
                  stroke={CHART_SERIES[0]}
                  strokeWidth={CHART_MARKS.lineWidth}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill={CHART_SERIES[0]}
                  fillOpacity={CHART_MARKS.areaOpacity}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        );
      })}
    </div>
  );
}
